import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Alert,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaSave,
  FaUndo,
  FaBullseye,
  FaShieldAlt,
  FaChartLine,
  FaEdit,
} from "react-icons/fa";

import { db } from "../firebase";
import Header from "../components/Header";

function CustomerTradeDetails() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [trades, setTrades] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [editingValues, setEditingValues] = useState({});
  const [openEditCard, setOpenEditCard] = useState({});

  const normalizeEmail = (value) => {
    return value?.trim().toLowerCase();
  };

  const loadTrades = async () => {
    const cleanCustomerId = normalizeEmail(customerId);

    const tradesQuery = query(
      collection(db, "customerTrades"),
      where("customerEmail", "==", cleanCustomerId)
    );

    const tradesSnapshot = await getDocs(tradesQuery);

    const tradesList = tradesSnapshot.docs.map((tradeDocument) => ({
      id: tradeDocument.id,
      ...tradeDocument.data(),
    }));

    tradesList.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

    setTrades(tradesList);

    if (tradesList.length > 0) {
      setCustomerName(tradesList[0].customerName || "");
      setCustomerEmail(tradesList[0].customerEmail || cleanCustomerId);
    } else {
      setCustomerEmail(cleanCustomerId);
    }

    const values = {};

    tradesList.forEach((trade) => {
      values[trade.id] = {
        investedAmount: trade.investedAmount || "",
        quantity: trade.quantity || "",
        manualProfitLossPercent: trade.manualProfitLossPercent || "",
        manualProfitLossAmount: trade.manualProfitLossAmount || "",
      };
    });

    setEditingValues(values);
  };

  useEffect(() => {
    loadTrades();
  }, [customerId]);

  const getProfitLoss = (trade) => {
    if (trade.isManualProfitLoss) {
      return {
        amount: Number(trade.manualProfitLossAmount || 0),
        percent: Number(trade.manualProfitLossPercent || 0),
        mode: "Manual",
      };
    }

    const entryPrice = Number(trade.entryPrice || 0);
    const currentPrice = Number(trade.currentPrice || entryPrice || 0);
    const quantity = Number(trade.quantity || 0);

    const amount = (currentPrice - entryPrice) * quantity;
    const percent =
      entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 : 0;

    return {
      amount: Number(amount.toFixed(2)),
      percent: Number(percent.toFixed(2)),
      mode: "Auto",
    };
  };

  const updateEditingValue = (tradeId, field, value) => {
    setEditingValues((previous) => ({
      ...previous,
      [tradeId]: {
        ...previous[tradeId],
        [field]: value,
      },
    }));
  };

  const toggleEditCard = (tradeId) => {
    setOpenEditCard((previous) => ({
      ...previous,
      [tradeId]: !previous[tradeId],
    }));
  };

  const confirmInvestment = async (trade) => {
    await updateDoc(doc(db, "customerTrades", trade.id), {
      investmentConfirmed: true,
      updatedAt: Date.now(),
    });

    alert("✅ Investment confirmed.");
    loadTrades();
  };

  const toggleTargetVisibility = async (trade) => {
    if (!trade.investmentConfirmed) {
      alert("Please confirm investment first, then allow Target.");
      return;
    }

    await updateDoc(doc(db, "customerTrades", trade.id), {
      showTargetToCustomer: !trade.showTargetToCustomer,
      updatedAt: Date.now(),
    });

    loadTrades();
  };

  const toggleStopLossVisibility = async (trade) => {
    if (!trade.investmentConfirmed) {
      alert("Please confirm investment first, then allow Stop Loss.");
      return;
    }

    await updateDoc(doc(db, "customerTrades", trade.id), {
      showStopLossToCustomer: !trade.showStopLossToCustomer,
      updatedAt: Date.now(),
    });

    loadTrades();
  };

  const saveTradeUpdate = async (trade) => {
    const values = editingValues[trade.id] || {};

    const investedAmount = Number(values.investedAmount || 0);
    const quantity = Number(values.quantity || 0);
    const manualProfitLossPercent = values.manualProfitLossPercent;
    const manualProfitLossAmount = values.manualProfitLossAmount;

    if (investedAmount <= 0 || quantity <= 0) {
      alert("Investment amount and quantity must be valid.");
      return;
    }

    const entryPrice = Number(trade.entryPrice || 0);
    const currentPrice = Number(trade.currentPrice || entryPrice || 0);

    const autoProfitLossAmount = (currentPrice - entryPrice) * quantity;
    const autoProfitLossPercent =
      entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 : 0;

    await updateDoc(doc(db, "customerTrades", trade.id), {
      investedAmount,
      quantity,

      manualProfitLossPercent,
      manualProfitLossAmount,
      isManualProfitLoss:
        manualProfitLossPercent !== "" || manualProfitLossAmount !== "",

      autoProfitLossAmount: Number(autoProfitLossAmount.toFixed(2)),
      autoProfitLossPercent: Number(autoProfitLossPercent.toFixed(2)),

      updatedAt: Date.now(),
    });

    alert("✅ Updated successfully.");
    loadTrades();
  };

  const resetProfitLossToAuto = async (trade) => {
    await updateDoc(doc(db, "customerTrades", trade.id), {
      manualProfitLossPercent: "",
      manualProfitLossAmount: "",
      isManualProfitLoss: false,
      updatedAt: Date.now(),
    });

    alert("✅ P/L reset to auto.");
    loadTrades();
  };

  const deleteTrade = async (trade) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this trade record?"
    );

    if (!confirmDelete) return;

    await deleteDoc(doc(db, "customerTrades", trade.id));

    alert("Trade deleted.");
    loadTrades();
  };

  const totalInvested = trades.reduce(
    (sum, trade) => sum + Number(trade.investedAmount || 0),
    0
  );

  const totalProfitLoss = trades.reduce((sum, trade) => {
    const profitLoss = getProfitLoss(trade);
    return sum + Number(profitLoss.amount || 0);
  }, 0);

  const confirmedTrades = trades.filter(
    (trade) => trade.investmentConfirmed === true
  ).length;

  return (
    <>
      <Header />

      <Container className="mt-4 mb-5">
        <Row className="mb-4 align-items-center">
          <Col xs={12} md={8}>
            <h2 className="fw-bold mb-1">Customer Trades</h2>

            <p className="text-muted mb-0">
              {customerName ? customerName : "Customer"} — {customerEmail}
            </p>
          </Col>

          <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
            <Button
              variant="outline-dark"
              className="w-100 w-md-auto"
              onClick={() => navigate("/manage-customers")}
            >
              <FaArrowLeft className="me-2" />
              Back
            </Button>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col xs={6} md={3} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body className="p-3">
                <small className="text-muted">Trades</small>
                <h4 className="fw-bold mb-0">{trades.length}</h4>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={6} md={3} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body className="p-3">
                <small className="text-muted">Confirmed</small>
                <h4 className="fw-bold text-success mb-0">
                  {confirmedTrades}
                </h4>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={6} md={3} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body className="p-3">
                <small className="text-muted">Invested</small>
                <h4 className="fw-bold mb-0">₹{totalInvested}</h4>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={6} md={3} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body className="p-3">
                <small className="text-muted">P/L</small>
                <h4
                  className={
                    totalProfitLoss >= 0
                      ? "fw-bold text-success mb-0"
                      : "fw-bold text-danger mb-0"
                  }
                >
                  ₹{totalProfitLoss}
                </h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {trades.length === 0 ? (
          <Alert variant="info">
            This customer has not saved any investment details yet.
          </Alert>
        ) : (
          <Row>
            {trades.map((trade) => {
              const profitLoss = getProfitLoss(trade);
              const values = editingValues[trade.id] || {};
              const isEditOpen = openEditCard[trade.id];

              return (
                <Col xs={12} lg={6} className="mb-4" key={trade.id}>
                  <Card
                    className="shadow border-0 h-100"
                    style={{ borderRadius: "18px", overflow: "hidden" }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h4 className="fw-bold mb-2">{trade.stockName}</h4>

                          <Badge bg="info" text="dark" className="me-2">
                            {trade.callType}
                          </Badge>

                          <Badge
                            bg={
                              trade.callStatus === "Closed"
                                ? "secondary"
                                : "success"
                            }
                          >
                            {trade.callStatus || "Active"}
                          </Badge>
                        </div>

                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => deleteTrade(trade)}
                        >
                          <FaTrash />
                        </Button>
                      </div>

                      <Row className="g-2 mb-3">
                        <Col xs={6}>
                          <div className="p-2 bg-light rounded">
                            <small className="text-muted">Buy Text</small>
                            <h6 className="fw-bold mb-0">
                              {trade.buyPrice || "-"}
                            </h6>
                          </div>
                        </Col>

                        <Col xs={6}>
                          <div className="p-2 bg-light rounded">
                            <small className="text-muted">Entry</small>
                            <h6 className="fw-bold mb-0">
                              ₹{trade.entryPrice || "-"}
                            </h6>
                          </div>
                        </Col>

                        <Col xs={6}>
                          <div className="p-2 bg-light rounded">
                            <small className="text-muted">Current</small>
                            <h6 className="fw-bold mb-0">
                              ₹{trade.currentPrice || "-"}
                            </h6>
                          </div>
                        </Col>

                        <Col xs={6}>
                          <div className="p-2 bg-light rounded">
                            <small className="text-muted">Investment</small>
                            <h6 className="fw-bold mb-0">
                              ₹{trade.investedAmount || 0}
                            </h6>
                          </div>
                        </Col>

                        <Col xs={6}>
                          <div className="p-2 bg-light rounded">
                            <small className="text-muted">Qty</small>
                            <h6 className="fw-bold mb-0">
                              {trade.quantity || 0}
                            </h6>
                          </div>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-between align-items-center bg-light rounded p-2 mb-3">
                        <div>
                          <small className="text-muted">Profit / Loss</small>
                          <h5
                            className={
                              profitLoss.amount >= 0
                                ? "fw-bold text-success mb-0"
                                : "fw-bold text-danger mb-0"
                            }
                          >
                            ₹{profitLoss.amount}
                          </h5>
                          <small className="text-muted">
                            {profitLoss.percent}% • {profitLoss.mode}
                          </small>
                        </div>

                        <FaChartLine
                          className={
                            profitLoss.amount >= 0
                              ? "text-success fs-4"
                              : "text-danger fs-4"
                          }
                        />
                      </div>

                      <Row className="g-2 mb-3">
                        <Col xs={12} md={4}>
                          {trade.investmentConfirmed ? (
                            <Button
                              size="sm"
                              variant="success"
                              className="w-100"
                              disabled
                            >
                              <FaCheckCircle className="me-1" />
                              Confirmed
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="success"
                              className="w-100"
                              onClick={() => confirmInvestment(trade)}
                            >
                              <FaCheckCircle className="me-1" />
                              Confirm
                            </Button>
                          )}
                        </Col>

                        <Col xs={6} md={4}>
                          <Button
                            size="sm"
                            className="w-100"
                            variant={
                              trade.showTargetToCustomer
                                ? "primary"
                                : "outline-primary"
                            }
                            onClick={() => toggleTargetVisibility(trade)}
                          >
                            {trade.showTargetToCustomer ? (
                              <>
                                <FaEye className="me-1" />
                                Target
                              </>
                            ) : (
                              <>
                                <FaEyeSlash className="me-1" />
                                Target
                              </>
                            )}
                          </Button>
                        </Col>

                        <Col xs={6} md={4}>
                          <Button
                            size="sm"
                            className="w-100"
                            variant={
                              trade.showStopLossToCustomer
                                ? "danger"
                                : "outline-danger"
                            }
                            onClick={() => toggleStopLossVisibility(trade)}
                          >
                            {trade.showStopLossToCustomer ? (
                              <>
                                <FaEye className="me-1" />
                                SL
                              </>
                            ) : (
                              <>
                                <FaEyeSlash className="me-1" />
                                SL
                              </>
                            )}
                          </Button>
                        </Col>
                      </Row>

                      <div className="small text-muted mb-3">
                        <FaBullseye className="me-1" />
                        Target: {trade.targetPrice || "-"}
                        <span className="mx-2">|</span>
                        <FaShieldAlt className="me-1" />
                        SL: {trade.stopLoss || "-"}
                      </div>

                      <Button
                        variant={isEditOpen ? "dark" : "outline-dark"}
                        size="sm"
                        className="w-100 mb-3"
                        onClick={() => toggleEditCard(trade.id)}
                      >
                        <FaEdit className="me-2" />
                        {isEditOpen ? "Hide Admin Edit" : "Admin Edit"}
                      </Button>

                      {isEditOpen && (
                        <Card
                          className="border-0"
                          style={{ background: "#f8fafc" }}
                        >
                          <Card.Body className="p-3">
                            <Row className="g-2">
                              <Col xs={6}>
                                <Form.Group className="mb-2">
                                  <Form.Label className="small">
                                    Investment
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    value={values.investedAmount || ""}
                                    onChange={(event) =>
                                      updateEditingValue(
                                        trade.id,
                                        "investedAmount",
                                        event.target.value
                                      )
                                    }
                                  />
                                </Form.Group>
                              </Col>

                              <Col xs={6}>
                                <Form.Group className="mb-2">
                                  <Form.Label className="small">Qty</Form.Label>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    value={values.quantity || ""}
                                    onChange={(event) =>
                                      updateEditingValue(
                                        trade.id,
                                        "quantity",
                                        event.target.value
                                      )
                                    }
                                  />
                                </Form.Group>
                              </Col>

                              <Col xs={6}>
                                <Form.Group className="mb-2">
                                  <Form.Label className="small">
                                    P/L %
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    placeholder="Optional"
                                    value={values.manualProfitLossPercent || ""}
                                    onChange={(event) =>
                                      updateEditingValue(
                                        trade.id,
                                        "manualProfitLossPercent",
                                        event.target.value
                                      )
                                    }
                                  />
                                </Form.Group>
                              </Col>

                              <Col xs={6}>
                                <Form.Group className="mb-2">
                                  <Form.Label className="small">
                                    P/L ₹
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    placeholder="Optional"
                                    value={values.manualProfitLossAmount || ""}
                                    onChange={(event) =>
                                      updateEditingValue(
                                        trade.id,
                                        "manualProfitLossAmount",
                                        event.target.value
                                      )
                                    }
                                  />
                                </Form.Group>
                              </Col>

                              <Col xs={6}>
                                <Button
                                  variant="warning"
                                  size="sm"
                                  className="w-100"
                                  onClick={() => saveTradeUpdate(trade)}
                                >
                                  <FaSave className="me-1" />
                                  Save
                                </Button>
                              </Col>

                              <Col xs={6}>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="w-100"
                                  onClick={() => resetProfitLossToAuto(trade)}
                                >
                                  <FaUndo className="me-1" />
                                  Auto
                                </Button>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>
    </>
  );
}

export default CustomerTradeDetails;